import { useParams } from "react-router-dom";
import { Link } from "react-router-dom";
import { useEffect } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import NotFound from "@/pages/NotFound";
import { getBlogPost } from "@/mocks/blogPosts";
import SEO from "@/components/base/SEO";

export default function BlogPost() {
  const { slug } = useParams<{ slug: string }>();
  const siteUrl = import.meta.env.VITE_SITE_URL || "https://acbuyspreadsheet.beer";

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [slug]);

  if (!slug) return <NotFound />;

  const post = getBlogPost(slug);
  if (!post) return <NotFound />;

  const postUrl = `${siteUrl}/blog/${post.slug}`;
  const seoKeywords = `acbuyspreadsheet, acbuy spreadsheet, ${post.category.toLowerCase()}, product sourcing, ecommerce spreadsheet, dropshipping`;

  return (
    <>
      <SEO
        title={`${post.title} | ACBuySpreadsheet`}
        description={post.excerpt}
        keywords={seoKeywords}
        canonicalUrl={postUrl}
        ogTitle={post.title}
        ogDescription={post.excerpt}
        ogUrl={postUrl}
        ogType="article"
        ogImage={post.heroImage}
        twitterTitle={post.title}
        twitterDescription={post.excerpt}
        twitterImage={post.heroImage}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "BlogPosting",
          "headline": post.title,
          "description": post.excerpt,
          "url": postUrl,
          "datePublished": "2026-06-24T00:00:00+00:00",
          "dateModified": "2026-06-24T00:00:00+00:00",
          "author": {
            "@type": "Organization",
            "name": "ACBuySpreadsheet",
            "url": `${siteUrl}/`,
          },
          "publisher": {
            "@type": "Organization",
            "name": "ACBuySpreadsheet",
            "url": `${siteUrl}/`,
          },
          "mainEntityOfPage": {
            "@type": "WebPage",
            "@id": postUrl,
          },
        }}
      />
      <Navbar />
      <main>
        <article>
          <header className="relative pt-32 pb-16 md:pt-40 md:pb-20 px-6 md:px-10 bg-background-100">
            <div className="max-w-[1280px] mx-auto">
              <div className="max-w-3xl mx-auto text-center">
                <span className="inline-block text-xs font-medium text-primary-600 uppercase tracking-widest mb-4 bg-primary-50/70 px-3 py-1 rounded-full">
                  {post.category}
                </span>
                <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-foreground-900 leading-tight">
                  {post.title}
                </h1>
                <p className="mt-5 text-sm md:text-base text-foreground-600 leading-relaxed max-w-2xl mx-auto">
                  {post.excerpt}
                </p>
                <div className="flex items-center justify-center gap-4 mt-6 text-xs text-foreground-500">
                  <span>Updated June 2026</span>
                  <span>&middot;</span>
                  <span>{post.readTime}</span>
                </div>
              </div>
            </div>
          </header>

          <div className="relative w-full h-[350px] md:h-[450px] overflow-hidden">
            <img
              src={post.heroImage}
              alt={post.title}
              title={post.title}
              className="w-full h-full object-cover object-top"
            />
          </div>

          <div className="py-12 md:py-16 px-6 md:px-10 bg-background-50">
            <div className="max-w-[1280px] mx-auto">
              <div className="flex flex-col lg:flex-row gap-10 lg:gap-16">
                <div className="lg:w-[65%]">
                  <div className="max-w-none">
                    {post.sections.map((section, sIdx) => (
                      <section key={sIdx} className="mb-10">
                        {section.heading && (
                          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-5">
                            {section.heading}
                          </h2>
                        )}
                        {section.subheading && (
                          <h3 className="font-heading text-lg md:text-xl font-semibold text-foreground-800 mt-6 mb-3">
                            {section.subheading}
                          </h3>
                        )}
                        {section.paragraphs.map((para, pIdx) => (
                          <p
                            key={pIdx}
                            className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4"
                          >
                            {para}
                          </p>
                        ))}
                      </section>
                    ))}
                  </div>

                  <div className="mt-12 p-6 md:p-8 rounded-lg bg-accent-50/50 border border-accent-200/50">
                    <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-3">
                      Ready to Transform Your Sourcing Workflow?
                    </h3>
                    <p className="text-sm text-foreground-600 mb-5">
                      Access the full {post.ctaText.toLowerCase()} on the main site and start organizing your product research today.
                    </p>
                    <a
                      href="https://kaspreadsheet.com/"
                      target="_blank"
                      rel="noopener nofollow"
                      className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-white font-semibold text-sm hover:bg-primary-600 transition-all duration-300 cursor-pointer whitespace-nowrap"
                    >
                      {post.ctaText}
                      <span className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-arrow-right-up-line"></i>
                      </span>
                    </a>
                  </div>

                  <nav className="mt-10 pt-8 border-t border-background-200" aria-label="Article navigation">
                    <Link
                      to="/"
                      className="inline-flex items-center gap-2 text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <span className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-arrow-left-line"></i>
                      </span>
                      Back to Homepage
                    </Link>
                  </nav>
                </div>

                <aside className="lg:w-[35%]">
                  <div className="sticky top-24 space-y-6">
                    <div className="p-5 rounded-lg bg-background-100">
                      <h4 className="font-heading text-sm font-semibold text-foreground-900 uppercase tracking-wider mb-3">
                        Article Details
                      </h4>
                      <ul className="space-y-2 text-sm text-foreground-600">
                        <li className="flex items-center gap-2">
                          <span className="w-4 h-4 flex items-center justify-center">
                            <i className="ri-bookmark-line text-xs"></i>
                          </span>
                          Category: {post.category}
                        </li>
                        <li className="flex items-center gap-2">
                          <span className="w-4 h-4 flex items-center justify-center">
                            <i className="ri-time-line text-xs"></i>
                          </span>
                          {post.readTime}
                        </li>
                      </ul>
                    </div>

                    <div className="p-5 rounded-lg bg-background-100">
                      <h4 className="font-heading text-sm font-semibold text-foreground-900 uppercase tracking-wider mb-3">
                        Explore Categories
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {["Shoes", "Hoodies", "T-Shirts", "Jackets", "Accessories", "Bags", "Jersey", "Sets"].map((cat) => (
                          <span key={cat} className="text-xs px-3 py-1.5 rounded-full bg-background-200/70 text-foreground-700 whitespace-nowrap">
                            {cat}
                          </span>
                        ))}
                      </div>
                    </div>

                    <div className="p-5 rounded-lg bg-background-100">
                      <h4 className="font-heading text-sm font-semibold text-foreground-900 uppercase tracking-wider mb-3">
                        Related Resources
                      </h4>
                      <ul className="space-y-3">
                        <li>
                          <Link to="/" className="text-sm text-primary-600 hover:text-primary-700 transition-colors cursor-pointer">
                            Homepage &mdash; Full Category Directory
                          </Link>
                        </li>
                        <li>
                          <a href="https://kaspreadsheet.com/" target="_blank" rel="noopener nofollow" className="text-sm text-primary-600 hover:text-primary-700 transition-colors cursor-pointer">
                            Main Site &mdash; Access the Full System
                          </a>
                        </li>
                      </ul>
                    </div>
                  </div>
                </aside>
              </div>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}